import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, CalendarCheck, Moon, Sun } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, WA_SCHEDULE } from "@/lib/constants";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 28, mass: 0.4 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  useEffect(() => () => document.documentElement.classList.remove("dark"), []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled || open
          ? "bg-white/90 backdrop-blur-md border-b border-cob-light/70 shadow-[0_8px_30px_-18px_rgba(6,63,50,0.25)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between transition-[height] duration-500 ${
            scrolled || open ? "h-16" : "h-[76px]"
          }`}
        >
          {isDark ? (
            <motion.a
              href="#inicio"
              data-testid="logo-link"
              className="flex items-center"
              initial={{ opacity: 0, scale: 0.15, rotate: -170 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 170, damping: 15 }}
            >
              <motion.img
                src="/logo-dark.png"
                alt="Clínica COB"
                initial={{ filter: "drop-shadow(0 0 0px rgba(213,166,66,0))" }}
                animate={{
                  filter: [
                    "drop-shadow(0 0 0px rgba(213,166,66,0))",
                    "drop-shadow(0 0 24px rgba(213,166,66,0.85))",
                    "drop-shadow(0 0 9px rgba(213,166,66,0.35))",
                  ],
                }}
                transition={{ duration: 1.5, times: [0, 0.45, 1], ease: "easeInOut" }}
                className={`${scrolled ? "h-10" : "h-12"} w-auto object-contain transition-[height] duration-500`}
              />
            </motion.a>
          ) : (
            <Logo compact={scrolled} />
          )}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-5" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className="nav-link text-[13px] font-medium tracking-wide text-cob-ink/80 hover:text-cob-green transition-colors duration-300"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="theme-toggle"
              aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
              title={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
              onClick={() => setIsDark((v) => !v)}
              className="btn-press inline-flex h-10 w-10 items-center justify-center rounded-full border border-cob-light bg-white/70 text-cob-dark transition-colors duration-300 hover:bg-cob-light"
            >
              {isDark ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
            </button>
            <a
              href={WA_SCHEDULE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="btn-press hidden sm:inline-flex items-center gap-2 rounded-full bg-cob-green px-5 py-2.5 text-[13px] font-semibold text-white transition-all duration-300 hover:bg-cob-dark hover:shadow-[0_10px_25px_-10px_rgba(6,63,50,0.5)]"
            >
              <CalendarCheck className="h-4 w-4" />
              Agende sua consulta
            </a>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={open ? "Fechar menu" : "Abrir menu"}
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-cob-light bg-white/70 text-cob-dark transition-transform duration-300 active:scale-90"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <motion.div
        data-testid="scroll-progress"
        className="absolute bottom-0 left-0 right-0 h-[2px] origin-left bg-gradient-to-r from-cob-gold/70 via-cob-gold to-cob-gold/70"
        style={{ scaleX: progress }}
      />

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-t border-cob-light/60"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="py-3 text-base font-medium text-cob-dark border-b border-cob-light/50 last:border-0"
                >
                  {link.label}
                </motion.a>
              ))}
              <a
                href={WA_SCHEDULE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-button"
                className="btn-press mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-cob-green px-6 py-4 text-sm font-semibold text-white"
              >
                <CalendarCheck className="h-4 w-4" />
                Agende sua consulta
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
